import React, { useState } from "react";
import {
  Modal,
  Steps,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Card,
  Table,
  InputNumber as AntInputNumber,
  message,
  Space,
} from "antd";
import { v4 as uuidv4 } from "uuid";

const { Step } = Steps;

export interface GroupStageManagerPopupProps {
  open: boolean;
  onClose: () => void;
  teams: { id: string; name: string }[];
  onSave: (data: GroupStageConfig) => void;
  maxTeamsPerGroup?: number;
}

export interface GroupStageConfig {
  groups: {
    name: string;
    teams: { id: string; name: string }[];
    matches: {
      id: string;
      team1Id: string;
      team2Id: string;
      score1?: number;
      score2?: number;
      court?: string; // ✅ thêm sân
    }[];
  }[];
}

const GroupStageManagerPopup: React.FC<GroupStageManagerPopupProps> = ({
  open,
  onClose,
  teams,
  onSave,
  maxTeamsPerGroup,
}) => {
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm<{ totalGroups: number }>();
  const [groups, setGroups] = useState<GroupStageConfig["groups"]>([]);

  const next = () => setCurrent((prev) => prev + 1);
  const prev = () => setCurrent((prev) => prev - 1);

  // ✅ Helper bỏ dấu & thường hóa text để search tiếng Việt tốt hơn
  const normalizeText = (text: string) =>
    text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  // ==== STEP 1: Tạo số bảng ====
  const Step1 = (
    <Form form={form} layout="vertical">
      <Form.Item
        name="totalGroups"
        label="Số bảng đấu"
        rules={[{ required: true, message: "Vui lòng nhập số bảng" }]}
      >
        <InputNumber min={1} max={16} style={{ width: 200 }} />
      </Form.Item>

      <Button
        type="primary"
        onClick={() => {
          form.validateFields().then(({ totalGroups }) => {
            const generated = Array.from({ length: totalGroups }, (_, i) => ({
              name: `Bảng ${String.fromCharCode(65 + i)}`,
              teams: [] as { id: string; name: string }[],
              matches: [] as GroupStageConfig["groups"][number]["matches"],
            }));
            setGroups(generated);
            next();
          });
        }}
      >
        Tiếp tục
      </Button>
    </Form>
  );

  // ==== Helpers ====
  const getAvailableTeamsForGroup = (groupIdx: number) => {
    const selectedInOtherGroups = new Set<string>();
    groups.forEach((g, i) => {
      if (i !== groupIdx) {
        g.teams.forEach((t) => selectedInOtherGroups.add(t.id));
      }
    });

    const currentTeamIds = new Set(groups[groupIdx]?.teams.map((t) => t.id));
    return teams.filter(
      (t) => !selectedInOtherGroups.has(t.id) || currentTeamIds.has(t.id)
    );
  };

  const updateGroupName = (groupIdx: number, name: string) => {
    setGroups((prev) => {
      const updated = [...prev];
      updated[groupIdx].name = name;
      return updated;
    });
  };

  const updateGroupTeams = (groupIdx: number, teamIds: string[]) => {
    setGroups((prev) => {
      const updated = [...prev];
      const trimmedIds =
        typeof maxTeamsPerGroup === "number"
          ? teamIds.slice(0, maxTeamsPerGroup)
          : teamIds;
      updated[groupIdx].teams = teams.filter((t) =>
        trimmedIds.includes(t.id)
      );
      return updated;
    });
  };

  const generateGroupMatches = () => {
    setGroups((prev) =>
      prev.map((g) => ({
        ...g,
        matches: createRoundRobinMatches(g.teams),
      }))
    );
  };

  const createRoundRobinMatches = (teamList: { id: string; name: string }[]) => {
    const matches: GroupStageConfig["groups"][number]["matches"] = [];
    for (let i = 0; i < teamList.length; i++) {
      for (let j = i + 1; j < teamList.length; j++) {
        matches.push({
          id: uuidv4(),
          team1Id: teamList[i].id,
          team2Id: teamList[j].id,
          score1: undefined,
          score2: undefined,
          court: "",
        });
      }
    }
    return matches;
  };

  const findTeamName = (groupIdx: number, teamId: string) =>
    groups[groupIdx].teams.find((t) => t.id === teamId)?.name || "";

  const updateMatchField = (
    groupIdx: number,
    matchId: string,
    field: "score1" | "score2" | "court",
    value?: any
  ) => {
    setGroups((prev) => {
      const updated = [...prev];
      updated[groupIdx].matches = updated[groupIdx].matches.map((m) =>
        m.id === matchId ? { ...m, [field]: value } : m
      );
      return updated;
    });
  };

  // ==== STEP 2: Nhập tên bảng + chọn đội ====
  const Step2 = (
    <>
      <p style={{ marginBottom: 12 }}>
        Nhập tên & chọn đội cho từng bảng (mỗi đội chỉ thuộc 1 bảng)
      </p>
      <Space direction="vertical" style={{ width: "100%" }}>
        {groups.map((group, idx) => {
          const availableTeams = getAvailableTeamsForGroup(idx);
          const chosen = group.teams.length;

          return (
            <Card
              key={`${group.name}-${idx}`}
              size="small"
              title={`Cấu hình ${group.name}`}
            >
              <Form layout="vertical">
                <Form.Item label="Tên bảng">
                  <Input
                    placeholder="Nhập tên bảng (VD: Bảng A)"
                    value={group.name}
                    onChange={(e) => updateGroupName(idx, e.target.value)}
                  />
                </Form.Item>
              </Form>

              {/* 🟢 Select có tìm kiếm đội */}
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Tìm và chọn đội..."
                style={{ width: "100%" }}
                value={group.teams.map((t) => t.id)}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  normalizeText(option?.label ?? "").includes(
                    normalizeText(input)
                  )
                }
                options={availableTeams.map((t) => ({
                  label: t.name,
                  value: t.id,
                  disabled:
                    typeof maxTeamsPerGroup === "number" &&
                    chosen >= maxTeamsPerGroup &&
                    !group.teams.some((gt) => gt.id === t.id),
                }))}
                onChange={(vals) => updateGroupTeams(idx, vals)}
              />
              <small style={{ color: "#999" }}>
                Đã chọn {group.teams.length}
                {typeof maxTeamsPerGroup === "number"
                  ? ` / ${maxTeamsPerGroup}`
                  : ""}{" "}
                đội
              </small>
            </Card>
          );
        })}
      </Space>

      <div style={{ marginTop: 16 }}>
        <Button onClick={prev} style={{ marginRight: 8 }}>
          Quay lại
        </Button>
        <Button
          type="primary"
          onClick={() => {
            const names = groups.map((g) => g.name.trim().toLowerCase());
            const hasDuplicate = names.some(
              (name, i) => names.indexOf(name) !== i
            );
            if (hasDuplicate) {
              message.error("Tên bảng bị trùng!");
              return;
            }

            const hasInvalid = groups.some(
              (g) => !g.name.trim() || g.teams.length < 2
            );
            if (hasInvalid) {
              message.warning("Mỗi bảng cần có tên và ít nhất 2 đội!");
              return;
            }

            generateGroupMatches();
            next();
          }}
        >
          Sinh trận đấu
        </Button>
      </div>
    </>
  );

  // ==== STEP 3: Nhập điểm & sân ====
  const Step3 = (
    <>
      <p>Nhập điểm và sân thi đấu cho từng trận</p>
      {groups.map((group, idx) => (
        <Card
          key={`${group.name}-${idx}`}
          title={group.name}
          size="small"
          style={{ marginBottom: 12 }}
        >
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            columns={[
              { title: "Trận", render: (_, __, i) => i + 1 },
              { title: "Đội 1", render: (r) => findTeamName(idx, r.team1Id) },
              {
                title: "Điểm đội 1",
                render: (r) => (
                  <AntInputNumber
                    min={0}
                    value={r.score1}
                    onChange={(v) =>
                      updateMatchField(idx, r.id, "score1", v ?? undefined)
                    }
                  />
                ),
              },
              { title: "Đội 2", render: (r) => findTeamName(idx, r.team2Id) },
              {
                title: "Điểm đội 2",
                render: (r) => (
                  <AntInputNumber
                    min={0}
                    value={r.score2}
                    onChange={(v) =>
                      updateMatchField(idx, r.id, "score2", v ?? undefined)
                    }
                  />
                ),
              },
              {
                title: "Sân thi đấu",
                render: (r) => (
                  <Input
                    placeholder="Nhập tên sân..."
                    value={r.court}
                    onChange={(e) =>
                      updateMatchField(idx, r.id, "court", e.target.value)
                    }
                  />
                ),
              },
            ]}
            dataSource={group.matches}
          />
        </Card>
      ))}

      <div style={{ marginTop: 16 }}>
        <Button onClick={prev} style={{ marginRight: 8 }}>
          Quay lại
        </Button>
        <Button
          type="primary"
          onClick={() => {
            const exportData: GroupStageConfig = {
              groups: groups.map((g) => ({
                name: g.name,
                teams: g.teams,
                matches: g.matches,
              })),
            };
            onSave(exportData);
            message.success("Đã lưu cấu hình vòng bảng!");
            onClose();
          }}
        >
          Lưu lại
        </Button>
      </div>
    </>
  );

  const steps = [
    { title: "Số bảng đấu", content: Step1 },
    { title: "Tên & đội của bảng", content: Step2 },
    { title: "Nhập điểm & sân", content: Step3 },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      bodyStyle={{ maxHeight: "72vh", overflowY: "auto" }}
      title="Cấu hình & Quản lý vòng bảng"
    >
      <Steps current={current} size="small" style={{ marginBottom: 24 }}>
        {steps.map((item) => (
          <Step key={item.title} title={item.title} />
        ))}
      </Steps>
      <div>{steps[current].content}</div>
    </Modal>
  );
};

export default GroupStageManagerPopup;
